import React, { useState, useEffect, useContext, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import {
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  MenuItem,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';
import buildBoardData from './utils/buildBoardData';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
  },
  button: {
    borderRadius: '10px',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    padding: '8px 12px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    borderRadius: '10px 10px 0 0',
  },
  filterInput: {
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
    minWidth: 180,
  },
  selectInput: {
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
    minWidth: 140,
  },
  dateInput: {
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
    marginRight: theme.spacing(1),
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(
    () => localStorage.getItem('sortOrder') || 'ticketNumber'
  );
  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterQueueId, setFilterQueueId] = useState('');
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Load users and queues for filters
  useEffect(() => {
    const loadFiltersData = async () => {
      try {
        const [uRes, qRes] = await Promise.all([
          api.get('/users', { params: { pageNumber: 1 } }),
          api.get('/queue'),
        ]);
        setUsers(uRes.data.users || []);
        setQueues(qRes.data || []);
      } catch {
        // silent
      }
    };
    loadFiltersData();
  }, []);

  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch {
        // silent
      } finally {
        setLoadingLaneOrder(false);
      }
    };
    if (user && user.id) loadLaneOrder();
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch {
      // silent
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate,
          endDate,
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch {
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    // Lane criada/deletada via Trello → recarrega tags
    const onTagChange = () => fetchTags();

    // Lanes reordenadas via Trello → atualiza ordem em tempo real
    const onKanbanReorder = data => {
      if (data.action === 'reorder' && Array.isArray(data.laneOrder)) {
        setLaneOrder(data.laneOrder);
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    socket.on(`company${companyId}-tag`, onTagChange);
    socket.on(`company${companyId}-kanban`, onKanbanReorder);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
      socket.off(`company${companyId}-tag`, onTagChange);
      socket.off(`company${companyId}-kanban`, onKanbanReorder);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const getOpportunityValue = ticket => {
    const fields = ticket.contact?.extraInfo || [];
    const f = fields.find(x => x.name === 'valor');
    return f ? parseFloat(f.value) : 0;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => getOpportunityValue(b) - getOpportunityValue(a));
    }

    const defaultTickets = sortedTickets.filter(t => t.tags.length === 0);

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => ({
        id: tag.id.toString(),
        title: tag.name,
        tickets: sortedTickets.filter(t => t.tags.some(tg => tg.id === tag.id)),
        color: tag.color || '#757575',
      })),
    ];

    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      laneMap.forEach(lane => orderedLanes.push(lane));
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) organizeLanes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder]);

  const boardData = useMemo(
    () => buildBoardData(lanes, searchText, filterUserId || null, filterQueueId || null),
    [lanes, searchText, filterUserId, filterQueueId]
  );

  const handleCardMove = async (cardId, targetLaneId, sourceLaneId) => {
    const ticketId = parseInt(cardId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);
      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success('Ticket Tag atualizado com sucesso!');
      } else {
        toast.success('Ticket Tag removido!');
      }
      fetchTickets();
    } catch {
      // silent
    }
  };

  const handleAddColumnClick = () => history.push('/tagsKanban');

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('Kanban')}</Title>
        <MainHeaderButtonsWrapper>
          <FormControl variant="outlined" size="small" className={classes.sortSelect}>
            <InputLabel htmlFor="sort-order-select">Ordenar por</InputLabel>
            <Select
              native
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              label="Ordenar por"
              inputProps={{ name: 'sortOrder', id: 'sort-order-select' }}
            >
              <option value="ticketNumber">Número do Ticket</option>
              <option value="lastMessageTime">Última Mensagem</option>
              <option value="valorDesc">Valor (maior para menor)</option>
            </Select>
          </FormControl>
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchTickets()}
            className={classes.button}
          >
            Buscar
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddColumnClick}
                className={classes.button}
              >
                + Adicionar colunas
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Filter bar */}
      <div className={classes.filterBar}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar contato ou mensagem..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className={classes.filterInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" variant="outlined" className={classes.selectInput}>
          <InputLabel>Atendente</InputLabel>
          <Select
            value={filterUserId}
            onChange={e => setFilterUserId(e.target.value)}
            label="Atendente"
          >
            <MenuItem value="">Todos</MenuItem>
            {users.map(u => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" className={classes.selectInput}>
          <InputLabel>Fila</InputLabel>
          <Select
            value={filterQueueId}
            onChange={e => setFilterQueueId(e.target.value)}
            label="Fila"
          >
            <MenuItem value="">Todas</MenuItem>
            {queues.map(q => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(searchText || filterUserId || filterQueueId) && (
          <Button
            size="small"
            onClick={() => {
              setSearchText('');
              setFilterUserId('');
              setFilterQueueId('');
            }}
            style={{ borderRadius: 8, textTransform: 'none', fontSize: '0.8rem' }}
          >
            Limpar filtros
          </Button>
        )}
      </div>

      <Paper variant="outlined" className={classes.mainPaper} style={{ borderRadius: '0 0 10px 10px', borderTop: 'none' }}>
        <KanbanBoard
          data={boardData}
          onCardMove={handleCardMove}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
